import React, { useState } from "react";
import { Moment } from "moment";
import _ from "lodash";
import cx from "classnames";
import styles from "./reportListView.module.scss";
import Tooltip from "../tooltip";
import InfiniteScroll from "components/ui/infiniteScrollLatest";
import { Icon } from "semantic-ui-react";

interface reportListViewProps {
  title: string;
  reportDate?: ReportDate;
  description?: string;
  items: any;
  canDownload?: boolean;
  downloadConfig?: {
    name: string;
    schema: any;
    fileName: string;
    title: string;
    reportType: string;
  };
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  renderListItem?: any;
  columns?: string[];
  actionHandler?: () => void;
  firstColumn?: string;
  viewMore?: () => void;
}

interface ReportDate {
  startDate: Moment;
  endDate: Moment;
  type?: string;
}
interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecordsCount: number;
}

const ReportListView: React.FC<reportListViewProps> = ({
  title,
  description,
  loading,
  items,
  pagination,
  loadMore,
  viewMore,
  renderListItem,
  columns,
  firstColumn,
}) => {
  const [showListItems, setShowListItems] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const handleViewMore = () => {
    viewMore?.();
    setShowAll(true);
    requestAnimationFrame(() => setShowListItems(true));
  };

  const handleCancelClick = () => {
    setShowListItems(false);
    setShowAll(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.report}>
          <div className={styles.textContainer}>
            <div className={styles.title}>{title}</div>
            <Tooltip placement="top" content={description}>
              <i className={cx(styles.paperfliteInfo, "paperflite-info")}></i>
            </Tooltip>
          </div>

          {items.length === 0 && !loading ? (
            <div className={styles.noData}>No data for the given filters.</div>
          ) : (
            <div className={styles.tableList}>
              <div className={styles.tableHeader}>
                <div className={styles.tabletitle}>{firstColumn}</div>
                <div className={styles.tableHead}>
                  {columns?.map((column, index) => (
                    <div key={index} className={styles.tableHeading}>
                      {column}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.scrollBody}>
                {items.slice(0, 5).map((item) => renderListItem({ item }))}
              </div>

              <div className={styles.actionWrapper}>
                {items.length > 5 && !showAll && (
                  <div className={styles.actionButton} onClick={handleViewMore}>
                    <Icon className={styles.rightArrow} name="arrow right" />
                    <span>view more</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showListItems ? (
        <div className={styles.overlay}>
          <ReportInsightsListView
            show={showListItems}
            title={title}
            description={description}
            items={items}
            loading={loading}
            pagination={pagination}
            loadMore={loadMore}
            onClose={handleCancelClick}
            renderItem={renderListItem}
            columns={columns}
            invalidateData={null}
            firstColumn={firstColumn}
          />
        </div>
      ) : null}

    </div>

  );
};

export default ReportListView;



interface ReportInsightsListViewProps {
  title: string;
  description?: string;
  items: any[];
  pagination: PaginationState;
  loading: boolean;
  loadMore: () => void;
  onClose: () => void;
  renderItem: any;
  columns?: string[];
  show: boolean;
  canDownload?: boolean;
  actionHandler?: () => void;
  baseFilters?: any;
  invalidateData: () => void;
  firstColumn?: string;
}

const ReportInsightsListView: React.FC<ReportInsightsListViewProps> = ({
  title,
  description,
  items,
  pagination,
  loadMore,
  onClose,
  loading,
  renderItem,
  columns,
  firstColumn,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleCloseClick = () => {
    setIsClosing(true);
  };

  const handleLoadMore = () => {
    if (!loading && pagination.currentPage < pagination.totalPages) {
      loadMore();
    }
  };

  const handleAnimationEnd = () => {
    if (isClosing) {
      onClose();
    }
  };

  const hasMore = () => {
    return (pagination.currentPage ?? 0) + 1 < (pagination.totalPages ?? 0);
  };

  return (
    <div className={cx(styles.customOffcanvas)}>
      <div className={cx(styles.scrollList, { [styles.slideOut]: isClosing })} onAnimationEnd={handleAnimationEnd}>
        <div className={styles.header}>
          <div className={styles.heading}>
            {title}
            <Tooltip placement="right" content={description}>
              <i className={cx(styles.paperfliteInfo, "paperflite-info")} />
            </Tooltip>
            <div className={styles.downloadContainer}>
              <div className={styles.closeContainer} onClick={handleCloseClick}>
                <i className="paperflite-close" />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.tableList}>
          <div className={styles.tableHeader}>
            <div className={styles.tabletitle}>{firstColumn}</div>
            <div className={styles.tableHead}>
              {columns?.map((column, index) => (
                <div key={index} className={styles.tableHeading}>
                  {column}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.scrollBody}>
            <InfiniteScroll
              className={styles.scroll}
              loading={loading}
              hasMore={hasMore()}
              loadMore={handleLoadMore}
              loader={() => (
                <div className={styles.loader}>loading more items...</div>
              )}
            >
              {items.map((item) => renderItem({ item }))}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    </div>
  );
};



